import React from 'react';

import { Button, H1, H3, HStack, Text, VStack } from '@betterment/samba-react';
import { SambaAutoLink } from '@betterment/shared-components';
import { useFallbackKeys } from '@betterment/shared-components/src/hooks/useFallbackKeys';

import AiOrb from './assets/icon_ai_orb.svg';
import { AccountHighlight } from './components/AccountHighlight';
import { EditAssumptionsToggleTip } from './components/EditAssumptionsToggleTip';
import { GradientBorderBox } from './components/GradientBorderBox';
import { LoadingReasoningAnimation } from './components/LoadingReasoningAnimation';
import { OpenAccountButton } from './components/OpenAccountButton';
import { AccountRecommendationOptionQuery } from './graphql/query.accountRecommendationOption.generated';
import { useAccountRecommendation } from './hooks/useAccountRecommendation';

type AccountRecommendationOption = NonNullable<AccountRecommendationOptionQuery['accountRecommendationOption']>;

type Props = { initialRecommendationOption: AccountRecommendationOption };
export const RecommendationContent: React.FC<Props> = ({ initialRecommendationOption }) => {
  const {
    currentRecommendation,
    isCreationLoading,
    isSkipLoading,
    createAccountRecommendation,
    skipAccountRecommendation,
  } = useAccountRecommendation({ initialRecommendationOption });
  const { option } = currentRecommendation;
  const reasonsToBelieve = useFallbackKeys(option.reasonsToBelieve);

  return (
    <VStack css={{ gap: 'space.300' }}>
      <VStack css={{ gap: 'space.100' }}>
        <HStack css={{ gap: 'space.50', alignItems: 'center' }}>
          <Text variant="smallCaps">RECOMMENDATION</Text>
          <EditAssumptionsToggleTip />
        </HStack>
        <H1 variant="brandTitle" color="text">
          {option.name}
        </H1>
        {reasonsToBelieve.map(({ item, key }) => (
          <AccountHighlight key={key} text={item} />
        ))}
      </VStack>
      {currentRecommendation.reasoning || isCreationLoading ? (
        <VStack css={{ gap: 'space.200', width: '100%' }}>
          <GradientBorderBox style={{ width: '100%' }}>
            <VStack css={{ padding: 'space.200', gap: 'space.200', width: '100%' }}>
              {currentRecommendation.reasoning ? (
                <>
                  <HStack css={{ gap: 'space.100', alignItems: 'center' }}>
                    <img src={AiOrb} alt="AI Icon" width="20px" height="20px" />
                    <H3 variant="title100" color="text">
                      Why this makes sense for you
                    </H3>
                  </HStack>
                  <Text
                    variant="text50"
                    color="text"
                    dangerouslySetInnerHTML={{ __html: currentRecommendation.reasoning }}
                  />
                  <Text variant="text25" color="text.weak">
                    AI-summary based on your financial profile.{' '}
                    <SambaAutoLink to="/foo" target="_blank">
                      Learn more
                    </SambaAutoLink>
                  </Text>
                </>
              ) : (
                <LoadingReasoningAnimation />
              )}
            </VStack>
          </GradientBorderBox>
        </VStack>
      ) : (
        <VStack css={{ gap: 'space.200' }}>
          <GradientBorderBox borderRadius={20}>
            <Button
              variant="textSecondary"
              css={{ borderRadius: '20px', textDecoration: 'none', background: 'none', padding: '1px' }}
              onClick={() => createAccountRecommendation()}
            >
              <HStack
                css={{
                  px: 'space.150',
                  py: 'space.100',
                  borderRadius: '20px',
                  background: 'background.surface',
                  gap: 'space.50',
                  alignItems: 'center',
                }}
              >
                <img src={AiOrb} alt="AI Icon" />
                <Text variant="text50" color="text" css={{ fontWeight: 'medium' }}>
                  Why is this account right for me?
                </Text>
              </HStack>
            </Button>
          </GradientBorderBox>
          <Text variant="text25">
            Generates an AI summary based on your financial profile. We never use your data to train our models.{' '}
            <SambaAutoLink to="/foo" target="_blank">
              Learn more
            </SambaAutoLink>
          </Text>
        </VStack>
      )}
      <HStack css={{ paddingTop: 'space.100', gap: 'space.100', alignItems: 'center' }}>
        <OpenAccountButton option={option} isEnabled={!isSkipLoading} />
        {currentRecommendation.isSkipEligible && (
          <Button variant="ghostPrimary" loading={isSkipLoading} onClick={() => skipAccountRecommendation()}>
            Next recommendation
          </Button>
        )}
      </HStack>
    </VStack>
  );
};
